/*
  La Matcha — filtered product lists (collection and search pages). Loaded after theme.js.

  <faceted-results data-section-id>   wraps the toolbar, the filter form and the results of a section
    and turns every change into a Section Rendering request, so the page never reloads:
      form[data-facets-form]      change / submit → URL built from the form, history.pushState
      a[data-facets-link]         chips, clear links, sort options, price buckets, page links → its href
                                  (data-facets-scroll also scrolls back to the top of the list)
      a[data-load-more]           appends the next page to [data-grid], history.replaceState
      [data-sidebar-toggle]       shows / hides the sidebar, remembered in localStorage
      details[data-sort-menu]     disclosure menu: arrow keys, Home, End, Escape, outside click
    Elements marked data-facets-region="<name>" are replaced by the element of the same name in the
    response. Focus returns to the control with the same data-focus-id, filter groups keep their
    open state, and the new result count (data-status on the element) is announced. New grid items
    and elements marked data-facets-enter fade in.
    Below 1100px the form moves from [data-facets-sidebar] into the sheet ([data-facets-slot]).
    Without JavaScript the same markup works as plain links and one form.

  Events on document
    lm:facets:updated   detail: { root, url, count }

  Also here
    [data-collection-helper]   hidden while its data-hide-param is part of the URL, or while the
                               list is empty (data-hide-when-empty)
    a[data-facets-remote]      a link outside the list that points at a filtered state of this page
    [data-collection-tabs]     scrolls the current tab into view when it starts outside the row
*/
(() => {
  const LM = (window.LM = window.LM || {});
  const STORAGE_KEY = 'lm:facets:sidebar';
  const WIDE = '(min-width: 1100px)';
  const parser = new DOMParser();

  const escapeValue = (value) => (window.CSS && CSS.escape ? CSS.escape(String(value)) : String(value).replace(/["\\]/g, '\\$&'));
  const reduceMotion = () => (LM.reduceMotion ? LM.reduceMotion() : false);
  const isVisible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  const plainClick = (event) =>
    !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  const readStored = () => {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  };

  const writeStored = (value) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (error) {
      // Storage is unavailable (private mode): the choice lasts for this page only.
    }
  };

  /* Shopify repeats the Section Rendering parameters in the URLs it generates; they never belong in a link. */
  const cleanUrl = (value) => {
    const url = new URL(value, window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    return url;
  };

  const pageKey = (url) => url.pathname + url.search;

  class FacetedResults extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId || '';
      this.form = this.querySelector('[data-facets-form]');
      this.sidebar = this.querySelector('[data-facets-sidebar]');
      this.dialog = this.querySelector('[data-facets-dialog]');
      this.sheetBody = this.dialog ? this.dialog.querySelector('[data-facets-slot]') : null;
      this.cache = new Map();
      this.currentKey = pageKey(window.location);
      this.pendingKey = null;
      this.controller = null;
      this.openerId = null;
      this.headerOffset = 0;
      this.wide = window.matchMedia(WIDE);

      if (!this.bound) {
        this.bound = true;
        this.addEventListener('change', (event) => this.onChange(event));
        this.addEventListener('submit', (event) => this.onSubmit(event));
        this.addEventListener('click', (event) => this.onClick(event));
        this.addEventListener('keydown', (event) => this.onKeydown(event));
        this.addEventListener('focusout', (event) => this.onFocusOut(event));
        this.addEventListener('toggle', (event) => this.onToggle(event), true);
      }

      this.onMedia = () => this.place();
      this.onPopState = () => {
        if (pageKey(window.location) === this.currentKey) return;
        this.navigate(window.location.href, { push: false });
      };
      this.onDocumentClick = (event) => {
        this.querySelectorAll('[data-sort-menu][open]').forEach((menu) => {
          if (!(event.target instanceof Node) || !menu.contains(event.target)) menu.open = false;
        });
      };
      this.onResize = LM.debounce ? LM.debounce(() => this.syncTop(), 150) : () => this.syncTop();

      if (this.wide.addEventListener) this.wide.addEventListener('change', this.onMedia);
      else this.wide.addListener(this.onMedia);
      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('resize', this.onResize);
      document.addEventListener('click', this.onDocumentClick);

      if (readStored() === 'hidden') this.setAttribute('data-sidebar', 'hidden');
      this.place();
      this.syncToggle();
      this.syncTop();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.wide) {
        if (this.wide.removeEventListener) this.wide.removeEventListener('change', this.onMedia);
        else this.wide.removeListener(this.onMedia);
      }
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('click', this.onDocumentClick);
    }

    /* ---------- Layout ---------- */

    /* One form: in the sidebar on wide screens, inside the sheet below. */
    place() {
      if (!this.form) return;
      const wide = this.wide.matches;
      const target = wide || !this.sheetBody ? this.sidebar : this.sheetBody;
      if (target && this.form.parentElement !== target) target.appendChild(this.form);
      if (wide && this.dialog && this.dialog.open) {
        if (LM.closeDialog) LM.closeDialog(this.dialog);
        else this.dialog.close();
      }
    }

    /* The toolbar sticks under the header only while the header itself is sticky. */
    syncTop() {
      const header = document.querySelector('[data-site-header]');
      const wrapper = header ? header.closest('.shopify-section') || header : null;
      const sticky = !!wrapper && window.getComputedStyle(wrapper).position === 'sticky';
      this.headerOffset = sticky ? Math.round(header.getBoundingClientRect().height) : 0;
      if (sticky) this.style.removeProperty('--facets-top');
      else this.style.setProperty('--facets-top', '0px');
    }

    setSidebar(visible) {
      if (visible) this.removeAttribute('data-sidebar');
      else this.setAttribute('data-sidebar', 'hidden');
      writeStored(visible ? 'shown' : 'hidden');
      this.syncToggle();
    }

    syncToggle() {
      const visible = this.getAttribute('data-sidebar') !== 'hidden';
      this.querySelectorAll('[data-sidebar-toggle]').forEach((button) => {
        button.setAttribute('aria-expanded', String(visible));
      });
    }

    scrollToTop() {
      const top = this.getBoundingClientRect().top + window.scrollY - this.headerOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    /* ---------- Events ---------- */

    onChange(event) {
      const target = event.target;
      if (!this.form || !(target instanceof HTMLElement) || target.form !== this.form) return;
      // Opened with the sort button, the sheet closes on a choice so the new order can be seen.
      // Opened for filtering it stays, several filters are usually set in a row.
      const closeAfter = target.getAttribute('name') === 'sort_by' && this.openerId === 'open-sort';
      this.navigate(this.formUrl()).then(() => {
        if (closeAfter && this.dialog && this.dialog.open) {
          if (LM.closeDialog) LM.closeDialog(this.dialog);
          else this.dialog.close();
        }
      });
    }

    onSubmit(event) {
      if (!this.form || event.target !== this.form) return;
      event.preventDefault();
      this.navigate(this.formUrl());
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;

      const opener = event.target.closest('[data-dialog-open]');
      if (opener) this.openerId = opener.getAttribute('data-focus-id');

      const toggle = event.target.closest('[data-sidebar-toggle]');
      if (toggle) {
        this.setSidebar(this.getAttribute('data-sidebar') === 'hidden');
        return;
      }

      if (!plainClick(event)) return;

      const more = event.target.closest('a[data-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
        return;
      }

      const link = event.target.closest('a[data-facets-link], .pagination a');
      if (!link || !link.getAttribute('href')) return;
      event.preventDefault();
      const menu = link.closest('[data-sort-menu]');
      if (menu) {
        // Focus moves to the button before the list (and the link in it) disappears.
        const summary = menu.querySelector('summary');
        if (summary) summary.focus({ preventScroll: true });
        menu.open = false;
      }
      const paging = link.hasAttribute('data-facets-scroll') || !!link.closest('.pagination');
      this.navigate(link.href, { scroll: paging, focusId: menu ? 'sort-menu' : null });
    }

    /* Sort menu: a disclosure of links with the keys people expect from a menu. */
    onKeydown(event) {
      if (!(event.target instanceof Element)) return;
      const menu = event.target.closest('[data-sort-menu]');
      if (!menu) return;
      const summary = menu.querySelector('summary');
      const options = [...menu.querySelectorAll('[data-sort-option]')];
      const index = options.indexOf(document.activeElement);
      const focus = (option) => {
        if (option) option.focus();
      };

      switch (event.key) {
        case 'Escape':
          if (!menu.open) return;
          event.preventDefault();
          menu.open = false;
          if (summary) summary.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!menu.open) {
            menu.open = true;
            focus(options.find((option) => option.hasAttribute('aria-current')) || options[0]);
          } else {
            focus(options[index + 1] || options[0]);
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!menu.open) menu.open = true;
          focus(index > 0 ? options[index - 1] : options[options.length - 1]);
          break;
        case 'Home':
          if (index < 0) return;
          event.preventDefault();
          focus(options[0]);
          break;
        case 'End':
          if (index < 0) return;
          event.preventDefault();
          focus(options[options.length - 1]);
          break;
        case ' ':
          if (index < 0) return;
          event.preventDefault();
          options[index].click();
          break;
        default:
      }
    }

    onFocusOut(event) {
      if (!(event.target instanceof Element) || !(event.relatedTarget instanceof Node)) return;
      const menu = event.target.closest('[data-sort-menu]');
      if (menu && menu.open && !menu.contains(event.relatedTarget)) menu.open = false;
    }

    /* The opening animation of a filter group plays for the visitor's own action only,
       not every time the list is re-rendered. */
    onToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.hasAttribute('data-facet')) return;
      details.classList.toggle('is-opening', details.open);
    }

    /* ---------- Requests ---------- */

    formUrl() {
      const form = this.form;
      const min = form.querySelector('input[type="number"][name$=".gte"]');
      const max = form.querySelector('input[type="number"][name$=".lte"]');
      if (min && max && min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)) {
        const swap = min.value;
        min.value = max.value;
        max.value = swap;
      }

      const params = new URLSearchParams();
      const defaultSort = form.getAttribute('data-default-sort');
      new FormData(form).forEach((value, name) => {
        if (typeof value !== 'string' || value === '') return;
        if (name === 'sort_by' && value === defaultSort) return;
        params.append(name, value);
      });
      const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.href);
      url.search = params.toString();
      return url.href;
    }

    async fetchRoot(target, signal) {
      const key = pageKey(target);
      let html = this.cache.get(key);
      if (!html) {
        const url = new URL(target.href);
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url.href, { signal });
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        html = await response.text();
        this.cache.set(key, html);
        if (this.cache.size > 24) this.cache.delete(this.cache.keys().next().value);
      }
      const doc = parser.parseFromString(html, 'text/html');
      const incoming =
        doc.querySelector(`faceted-results[data-section-id="${escapeValue(this.sectionId)}"]`) || doc.querySelector('faceted-results');
      if (!incoming) throw new Error('Section not found');
      incoming.querySelectorAll('a[href*="section_id="]').forEach((link) => {
        const url = cleanUrl(link.getAttribute('href'));
        link.setAttribute('href', url.pathname + url.search + url.hash);
      });
      return incoming;
    }

    async navigate(href, options = {}) {
      const { push = true, scroll = false, focusId = null } = options;
      const target = cleanUrl(href);
      if (target.origin !== window.location.origin || target.pathname !== window.location.pathname) {
        window.location.assign(target.href);
        return;
      }
      const key = pageKey(target);
      if (key === this.pendingKey) return;
      if (push && key === this.currentKey) {
        if (scroll) this.scrollToTop();
        return;
      }

      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.pendingKey = key;
      this.setBusy(true);

      try {
        const incoming = await this.fetchRoot(target, controller.signal);
        if (controller.signal.aborted) return;
        this.render(incoming, { focusId, scroll });
        this.currentKey = key;
        if (push) window.history.pushState({ lmFacets: this.sectionId }, '', target.href);
        if (scroll) this.scrollToTop();
        this.notify(target, this.dataset.status);
      } catch (error) {
        if (controller.signal.aborted || (error && error.name === 'AbortError')) return;
        // Anything unexpected: let the browser load the page the classic way.
        window.location.assign(target.href);
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.pendingKey = null;
          this.setBusy(false);
        }
      }
    }

    async loadMore(link) {
      if (link.classList.contains('is-loading')) return;
      const target = cleanUrl(link.href);
      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      link.classList.add('is-loading');
      link.setAttribute('aria-disabled', 'true');

      try {
        const incoming = await this.fetchRoot(target, controller.signal);
        if (controller.signal.aborted) return;
        const grid = this.querySelector('[data-grid]');
        const source = incoming.querySelector('[data-grid]');
        if (!grid || !source) throw new Error('Grid not found');

        const progress = this.readProgress();
        const items = [...source.children];
        items.forEach((item) => {
          item.classList.add('is-entering');
          grid.appendChild(item);
        });
        this.swapRegions(incoming, ['pager']);
        this.animateProgress(progress);

        this.currentKey = pageKey(target);
        window.history.replaceState(window.history.state, '', target.href);

        const first = items[0] ? items[0].querySelector('a[href]:not([tabindex="-1"]), button:not([disabled])') : null;
        if (first) first.focus({ preventScroll: true });
        const label = this.querySelector('[data-facets-region="pager"] p');
        this.notify(target, label ? label.textContent.trim() : '');
      } catch (error) {
        if (controller.signal.aborted || (error && error.name === 'AbortError')) return;
        window.location.assign(target.href);
      } finally {
        if (this.controller === controller) this.controller = null;
        if (document.contains(link)) {
          link.classList.remove('is-loading');
          link.removeAttribute('aria-disabled');
        }
      }
    }

    /* ---------- Rendering ---------- */

    setBusy(state) {
      this.classList.toggle('is-loading', state);
      if (state) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
    }

    readProgress() {
      const bar = this.querySelector('[data-pager-bar]');
      return bar ? bar.style.getPropertyValue('--value').trim() : '';
    }

    /* Lets the progress bar travel from its previous value instead of jumping. */
    animateProgress(previous) {
      const bar = this.querySelector('[data-pager-bar]');
      if (!bar || !previous || !bar.firstElementChild) return;
      const next = bar.style.getPropertyValue('--value').trim();
      if (next === previous) return;
      bar.style.setProperty('--value', previous);
      void bar.firstElementChild.offsetWidth;
      bar.style.setProperty('--value', next);
    }

    swapRegions(incoming, only) {
      const sources = new Map();
      incoming.querySelectorAll('[data-facets-region]').forEach((el) => sources.set(el.getAttribute('data-facets-region'), el));
      this.querySelectorAll('[data-facets-region]').forEach((el) => {
        const name = el.getAttribute('data-facets-region');
        const source = sources.get(name);
        if (!source || (only && !only.includes(name))) return;
        if (el.innerHTML !== source.innerHTML) {
          el.innerHTML = source.innerHTML;
          if (name === 'results') {
            el.querySelectorAll('[data-grid] > *, [data-facets-enter]').forEach((item) => item.classList.add('is-entering'));
          }
        }
        el.hidden = source.hidden;
      });
    }

    find(id) {
      if (!id) return null;
      return [...this.querySelectorAll(`[data-focus-id="${escapeValue(id)}"]`)].find(isVisible) || null;
    }

    render(incoming, { focusId = null, scroll = false } = {}) {
      const active = document.activeElement;
      const inside = !!active && active !== document.body && this.contains(active);
      const holder = inside ? active.closest('[data-focus-id]') : null;
      const previousId = holder ? holder.getAttribute('data-focus-id') : null;
      const open = new Map();
      this.querySelectorAll('details[data-facet]').forEach((details) => open.set(details.getAttribute('data-facet'), details.open));
      const progress = this.readProgress();

      this.swapRegions(incoming);
      ['data-count', 'data-status'].forEach((name) => {
        if (incoming.hasAttribute(name)) this.setAttribute(name, incoming.getAttribute(name));
        else this.removeAttribute(name);
      });

      this.querySelectorAll('details[data-facet]').forEach((details) => {
        const key = details.getAttribute('data-facet');
        if (open.has(key)) details.open = open.get(key);
      });
      this.syncToggle();
      this.animateProgress(progress);

      // The sheet remembers which button opened it; that button has just been replaced.
      if (this.dialog && this.dialog.open && this.dialog._opener && !document.contains(this.dialog._opener)) {
        this.dialog._opener = this.find(this.openerId) || this.find('open-filters') || this.find('open-sort');
      }

      // Focus: back to the same control when it was replaced, to the list after paging.
      const current = document.activeElement;
      const lost = !current || current === document.body || !document.contains(current);
      if (scroll) {
        const first = this.querySelector('[data-facets-region="results"] a[href]:not([tabindex="-1"])');
        if (first) first.focus({ preventScroll: true });
        return;
      }
      if (!inside || (!lost && !focusId)) return;
      let target = this.find(focusId || previousId);
      if (!target && previousId && previousId.startsWith('chip:')) {
        target = this.find(`chip:${Number(previousId.slice(5)) - 1}`);
      }
      if (!target && this.dialog && this.dialog.open) target = this.find('sheet-done');
      if (!target) target = this.find('sidebar-toggle') || this.find('open-filters') || this.find('sort-menu') || this.find('open-sort');
      if (target) target.focus({ preventScroll: true });
    }

    notify(target, message) {
      if (message) {
        if (LM.announce) LM.announce(message);
        // The page-level live region is inert while the sheet is open as a modal dialog.
        const region = this.dialog && this.dialog.open ? this.dialog.querySelector('[data-live-region]') : null;
        if (region) {
          region.textContent = '';
          window.setTimeout(() => {
            region.textContent = message;
          }, 100);
        }
      }
      document.dispatchEvent(
        new CustomEvent('lm:facets:updated', {
          detail: { root: this, url: target.href, count: Number(this.dataset.count || 0) },
        })
      );
    }
  }

  if (!customElements.get('faceted-results')) customElements.define('faceted-results', FacetedResults);

  /* ---------- Decision helper band ---------- */
  const syncHelpers = () => {
    const params = new URLSearchParams(window.location.search);
    const root = document.querySelector('faceted-results');
    const empty = !!root && Number(root.dataset.count || 0) === 0;
    document.querySelectorAll('[data-collection-helper]').forEach((el) => {
      const name = el.getAttribute('data-hide-param');
      let hide = el.hasAttribute('data-hide-when-empty') && empty;
      if (name) {
        params.forEach((value, key) => {
          if (key === name || key.startsWith(`${name}.`)) hide = true;
        });
      }
      el.hidden = hide;
    });
  };

  document.addEventListener('lm:facets:updated', syncHelpers);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || !plainClick(event)) return;
    const link = event.target.closest('a[data-facets-remote]');
    if (!link) return;
    const root = document.querySelector('faceted-results');
    if (!root || typeof root.navigate !== 'function') return;
    const target = cleanUrl(link.href);
    if (target.origin !== window.location.origin || target.pathname !== window.location.pathname) return;
    event.preventDefault();
    root.navigate(target.href, { scroll: true });
  });

  /* ---------- Collection tabs ---------- */
  const initTabs = (scope) => {
    scope.querySelectorAll('[data-collection-tabs]').forEach((nav) => {
      const current = nav.querySelector('[aria-current="page"]');
      if (!current || nav.scrollWidth <= nav.clientWidth) return;
      const box = nav.getBoundingClientRect();
      const tab = current.getBoundingClientRect();
      if (tab.left >= box.left && tab.right <= box.right) return;
      const offset = tab.left - box.left + nav.scrollLeft;
      nav.scrollLeft = Math.max(0, offset - (nav.clientWidth - tab.width) / 2);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initTabs(document));
  else initTabs(document);

  /* ---------- Theme editor ---------- */
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) initTabs(event.target);
    // A re-rendered section may have taken its open sheet with it.
    if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('has-dialog');
  });
})();
